import React, { useMemo, useState } from "react";
import {
  Title,
  Select,
  GoBack,
  TextArea,
  MyMessage,
  InputField,
  PhoneInput,
  MyActionButton,
  DashboardIntro,
  MyButton,
} from "../../home/component/Component";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../middlewares/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../home/component/Pricing";
import GenerateDescription from "../utils/GenerateDescription";

/* ---------- Utils ---------- */
const slugify = (s) =>
  s
    ? s
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
    : null;

const Add = () => {
  const { createOneContent } = useDoSeeDataContext();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
    tags,
  } = useGeoPositionContext();

  /* ---------- Form state ---------- */
  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");

  const [title, setTitle] = useState("");
  const [venue, setVenue] = useState("");
  const [venueCapacity, setVenueCapacity] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [email, setEmail] = useState("");
  const [payLink, setPayLink] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [whatsapp, setWhatsapp] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");
  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");

  const [hashtags, setHashtags] = useState("");
  const [googleMapCode, setGoogleMapCode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [formattedAddress, setFormattedAddress] = useState("");

  const [featured, setFeatured] = useState("no");
  const [promoted, setPromoted] = useState("no");
  const [expo, setExpo] = useState(0);
  const [cityguideStatus, setCityGuideStatus] = useState(1);

  const [description, setDescription] = useState("");
  const [pricing, setPricing] = useState([]);

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  /* ---------- UI state ---------- */
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const cityGuideRoutes = "/data/city/guide";

  /* ---------- Helpers ---------- */
  const resetNotices = () => {
    setError(false);
    setSuccess(false);
    setErrorMessage("");
  };

  const onField = (setter) => (e) => {
    setter(e.target.value);
    if (error || success) resetNotices();
  };

  const combinedTags = useMemo(
    () =>
      [tags, hashtags]
        .filter(Boolean)
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .join(", "),
    [tags, hashtags]
  );

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);

    setThumbnail(defaultImage);
    setThumbnailId("img_cdn_id");

    setTitle("");
    setVenue("");
    setVenueCapacity("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");

    setEmail("");
    setPayLink(null);
    setPhoneNumber("");

    setWhatsapp("");
    setFacebook("");
    setTwitter("");
    setInstagram("");
    setYoutube("");

    setHashtags("");
    setGoogleMapCode("");
    setLatitude("");
    setLongitude("");
    setFormattedAddress("");

    setFeatured("no");
    setPromoted("no");
    setExpo(0);
    setCityGuideStatus(1);

    setDescription("");
    setPricing([]);
  };

  const respond = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg || "");
  };

  /* ---------- Submit ---------- */
  const addOneCityGuideHandler = async (e) => {
    e.preventDefault();
    resetNotices();
    setLoading(true);

    // Minimal validations
    if (!thumbnail || !thumbnailId) return respond(true, "Image required.");
    if (!title?.trim()) return respond(true, "Title is required.");
    if (!venue?.trim()) return respond(true, "Venue is required.");
    if (!startDate || !endDate || !startTime || !endTime)
      return respond(true, "Start/End date and time are required.");

    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      country_id,
      first_level_id: first_level_id || 0,
      second_level_id: second_level_id || 0,
      third_level_id: third_level_id || 0,
      fourth_level_id: fourth_level_id || 0,

      title: title?.trim(),
      slug: slugify(title),

      description: description || "",
      venue: venue || "",
      venue_capacity: venueCapacity ? Number(venueCapacity) : 0,

      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,

      thumbnail: thumbnail || defaultImage,
      image_cdn_id: thumbnailId || "",

      phone_number: phoneNumber || "",
      email_address: email || "",
      pay_link: payLink,

      whatsapp: whatsapp || "none",
      facebook: facebook || "none",
      twitter: twitter || "none",
      instagram: instagram || "none",
      youtube: youtube || "none",

      hashtag: combinedTags || "none",
      audio_adv: "none",

      google_map_code: googleMapCode || "none",
      latitude: latitude || "none",
      longitude: longitude || "none",
      formatted_address: formattedAddress || "",

      country_short: "none",
      status: cityguideStatus,
      featured,
      promoted,
      expo,

      pricing,
    };

    const { status, message } = await createOneContent({
      data,
      content: _TYPES_.CITYGUIDE,
    });

    if (status === 200 || status === 201) {
      cleanFields();
      respond(false, message);
    } else {
      respond(true, message);
    }
  };

  /* ---------- Layout tokens ---------- */
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-6";
  const grid3 = "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";
  const grid4 = "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4";

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        <BreadCrumbComponent />
        <GoBack props={{ route: cityGuideRoutes }} />

        {showDescriptionModal && (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        )}

        <div className="my-4">
          <DashboardIntro
            props={{ intro: "Add City Guide", name: entry_label }}
          />
        </div>

        <form onSubmit={addOneCityGuideHandler}>
          <div className={`${card} ${sectionPad} space-y-6`}>
            <MyMessage
              error={error}
              message={message}
              success={success}
              onExpired={() => {
                setError(false);
                setSuccess(false);
              }}
              onClick={resetNotices}
            />

            {/* Media */}
            <ImageKitUpload
              thumbnail={thumbnail}
              thumbnailId={thumbnailId}
              setThumbnail={setThumbnail}
              setThumbnailId={setThumbnailId}
              folder={image_kit_folder(country_name, FOLDERS.CITY_GUIDE)}
            />

            {/* Details */}
            <div>
              <Title props={{ title: "City Guide Details" }} />
              <div className={grid3}>
                <InputField
                  props={{
                    id: "title",
                    title: "Title",
                    type: "text",
                    required: true,
                    placeholder: "e.g., Weekend Food Festival",
                    value: title,
                    onChange: onField(setTitle),
                  }}
                />
                <InputField
                  props={{
                    id: "venue",
                    title: "Venue",
                    type: "text",
                    required: true,
                    placeholder: "e.g., Central Park Arena",
                    value: venue,
                    onChange: onField(setVenue),
                  }}
                />
                <InputField
                  props={{
                    id: "venue_capacity",
                    title: "Venue Capacity",
                    type: "number",
                    placeholder: "e.g., 2000",
                    value: venueCapacity,
                    onChange: onField(setVenueCapacity),
                  }}
                />
                <InputField
                  props={{
                    id: "formatted_address",
                    title: "Formatted Address",
                    type: "address",
                    placeholder: "123 Main St, City",
                    value: formattedAddress,
                    onChange: onField(setFormattedAddress),
                  }}
                />
              </div>
            </div>

            {/* Date & Time */}
            <div>
              <Title props={{ title: "Date & Time" }} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <InputField
                  props={{
                    id: "start_date",
                    title: "Start Date",
                    type: "date",
                    required: true,
                    value: startDate,
                    onChange: onField(setStartDate),
                  }}
                />
                <InputField
                  props={{
                    id: "end_date",
                    title: "End Date",
                    type: "date",
                    required: true,
                    value: endDate,
                    onChange: onField(setEndDate),
                  }}
                />
                <InputField
                  props={{
                    id: "start_time",
                    title: "Start Time",
                    type: "time",
                    required: true,
                    value: startTime,
                    onChange: onField(setStartTime),
                  }}
                />
                <InputField
                  props={{
                    id: "end_time",
                    title: "End Time",
                    type: "time",
                    required: true,
                    value: endTime,
                    onChange: onField(setEndTime),
                  }}
                />
              </div>
            </div>

            {/* Pricing */}
            <div>
              <Title props={{ title: "Pricing" }} />
              <Pricing pricing={pricing} setPricing={setPricing} />
            </div>

            {/* Contact */}
            <div>
              <Title props={{ title: "Contact Info" }} />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="flex flex-row justify-center items-center">
                  <PhoneInput
                    setPhone={setPhoneNumber}
                    phone={phoneNumber}
                    required={true}
                  />
                </div>
                <InputField
                  props={{
                    id: "email",
                    title: "Email Address",
                    type: "email",
                    required: true,
                    placeholder: "contact@example.com",
                    value: email,
                    onChange: onField(setEmail),
                  }}
                />
                <InputField
                  props={{
                    id: "pay-link",
                    title: "Pay Link (Optional)",
                    type: "text",
                    value: payLink ? payLink : null,
                    onChange: (e) => {
                      setPayLink(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
              </div>
            </div>

            {/* Social */}
            <div>
              <Title props={{ title: "Social Contact" }} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <InputField
                  props={{
                    id: "whatsapp",
                    title: "Whatsapp",
                    type: "text",
                    value: whatsapp,
                    onChange: onField(setWhatsapp),
                  }}
                />
                <InputField
                  props={{
                    id: "facebook",
                    title: "Facebook",
                    type: "text",
                    value: facebook,
                    onChange: onField(setFacebook),
                  }}
                />
                <InputField
                  props={{
                    id: "twitter",
                    title: "Twitter",
                    type: "text",
                    value: twitter,
                    onChange: onField(setTwitter),
                  }}
                />
                <InputField
                  props={{
                    id: "instagram",
                    title: "Instagram",
                    type: "text",
                    value: instagram,
                    onChange: onField(setInstagram),
                  }}
                />
                <InputField
                  props={{
                    id: "youtube",
                    title: "YouTube",
                    type: "text",
                    value: youtube,
                    onChange: onField(setYoutube),
                  }}
                />
              </div>
            </div>

            {/* Geo */}
            <div>
              <Title props={{ title: "Geo Position" }} />
              <div className={grid4}>
                <InputField
                  props={{
                    id: "google_map_code",
                    title: "Google Map Code",
                    type: "text",
                    placeholder: "<iframe ...>",
                    value: googleMapCode,
                    onChange: onField(setGoogleMapCode),
                  }}
                />
                <InputField
                  props={{
                    id: "latitude",
                    title: "Latitude",
                    type: "text",
                    value: latitude,
                    onChange: onField(setLatitude),
                  }}
                />
                <InputField
                  props={{
                    id: "longitude",
                    title: "Longitude",
                    type: "text",
                    value: longitude,
                    onChange: onField(setLongitude),
                  }}
                />
              </div>
            </div>

            {/* Settings */}
            <div>
              <Title props={{ title: "More Settings" }} />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <Select
                  props={{
                    id: "cityguide_status",
                    title: "Status",
                    option: ["visible", "invisible"],
                    onChange: (e) => {
                      setCityGuideStatus(e.target.selectedIndex === 0 ? 1 : 0);
                      if (error || success) resetNotices();
                    },
                  }}
                />
                <Select
                  props={{
                    id: "featured",
                    title: "Featured",
                    option: ["no", "yes"],
                    onChange: (e) => {
                      setFeatured(e.target.value);
                      if (error || success) resetNotices();
                    },
                  }}
                />
                <Select
                  props={{
                    id: "promoted",
                    title: "Promoted",
                    option: ["no", "yes"],
                    onChange: (e) => {
                      setPromoted(e.target.value);
                      if (error || success) resetNotices();
                    },
                  }}
                />
                <Select
                  props={{
                    id: "gallery",
                    title: "Show in Gallery",
                    option: ["no", "yes"],
                    onChange: (e) => {
                      const choice = e.target.value;
                      setExpo(choice === "yes" ? 1 : 0);
                      if (error || success) resetNotices();
                    },
                  }}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between">
                <Title props={{ title: "Description" }} />
                <MyButton
                  onClick={() => setShowDescriptionModal(true)}
                  className="bg-blue-700 hover:bg-blue-600 py-2 px-3 rounded-lg text-white"
                >
                  Generate Description
                </MyButton>
              </div>
              <TextArea
                props={{
                  id: "description",
                  title: "Description",
                  type: "text",
                  value: description,
                  onChange: onField(setDescription),
                }}
              />
            </div>

            {/* Tags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                props={{
                  id: "hashtag",
                  title: "Tags (comma separated)",
                  type: "text",
                  placeholder: "music, food, open-air",
                  value: hashtags,
                  onChange: onField(setHashtags),
                }}
              />
            </div>

            {/* Footer actions */}
            <div className="flex justify-end gap-3 pt-2">
              <MyButton
                type="button"
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                onClick={cleanFields}
              >
                Reset
              </MyButton>
              <MyActionButton
                loading={loading}
                title="Add City Guide"
                onClick={resetNotices}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Add;
